import type { ButtonProps } from "@/components/ui/button";
import { Button } from "@/components/ui/button";
import * as React from "react";

/**
 * navigator.clipboard only exists in secure contexts (https, localhost) and is refused
 * inside cross-origin iframes. Dev hosts served over plain http — and the auth pages
 * embedded by the marketing site — hit both cases, so fall back to the legacy path.
 */
export async function writeToClipboard(text: string): Promise<void> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }
  } catch {
    // API present but refused; fall through to execCommand.
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  // Keep it off-screen without triggering a scroll jump on focus.
  textarea.style.position = "fixed";
  textarea.style.top = "0";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  try {
    if (!document.execCommand("copy")) {
      throw new Error("Clipboard unavailable: execCommand('copy') was rejected");
    }
  } finally {
    document.body.removeChild(textarea);
  }
}

export interface CopyButtonProps extends ButtonProps {
  onCopy?: () => void;
  onCopyFail?: () => void;
  value?: string;
  className?: string;
  disabled?: boolean;
}

export const CopyButton = React.forwardRef<HTMLButtonElement, React.PropsWithChildren<CopyButtonProps>>(
  ({ value, onCopy, onCopyFail, onClick, ...props }, ref) => {
    return (
      <Button
        ref={ref}
        {...props}
        onClick={async event => {
          if (value) {
            try {
              await writeToClipboard(value);
              onClick?.(event);
              onCopy?.();
            } catch (error) {
              console.error("Failed to copy to clipboard", error);
              onCopyFail?.();
            }
          }
        }}
      />
    );
  }
);

CopyButton.displayName = "CopyButton";
